import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { reasonsToConsult, consultationInfo } from '../mockData';
import { Card, CardContent } from './ui/card';
import { Clock, Euro, Info } from 'lucide-react';
import { Button } from './ui/button';
import { createStaggerContainer, fadeInLeft, fadeInRight, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

const ConsultationsSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.25 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.16, delayChildren: 0.1 });

  return (
    <motion.section id="consultations" className="py-12 sm:py-16 lg:py-20 bg-[#F5F0E8]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.div className="flex items-center justify-center gap-3 mb-4" variants={fadeInUp}>
            <motion.span
              className="h-px w-12 bg-gradient-to-r from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#a5400e] font-medium">
              Consultations
            </span>
            <motion.span
              className="h-px w-12 bg-gradient-to-l from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          </motion.div>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5c2810] mb-3 sm:mb-4 relative inline-block" variants={fadeInUp}>
            Mes Consultations
            <motion.span
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#a5400e] to-transparent rounded-full"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#7a4a3a] max-w-3xl mx-auto px-4" variants={fadeInUp}>
            Des prestations adaptées à vos besoins spécifiques pour vous accompagner vers un bien-être durable.
          </motion.p>
        </motion.div>

        <motion.div className="relative bg-white/70 backdrop-blur-sm p-8 sm:p-10 lg:p-12 rounded-2xl border border-[#a5400e]/10 shadow-[0_10px_30px_rgb(165,64,14,0.06)] sm:shadow-[0_20px_60px_rgb(165,64,14,0.08)] mb-8 sm:mb-12" variants={fadeInUp}>
          <div className="absolute -inset-1 bg-gradient-to-br from-[#a5400e]/5 via-[#d46a3e]/5 to-[#e8a87c]/5 rounded-2xl blur-2xl -z-10" />
          <div className="flex items-center gap-4 mb-6">
            <div className="w-1.5 h-12 bg-gradient-to-b from-[#a5400e] to-[#e8a87c] rounded-full" />
            <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5c2810]" variants={fadeInUp}>
              Pour quelles raisons consulter ?
            </motion.h3>
          </div>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6" variants={createStaggerContainer({ staggerChildren: 0.08 })}>
            {reasonsToConsult.map((reason, index) => (
              <motion.div key={index} className="flex items-center space-x-3 group" variants={fadeInLeft} whileHover={{ x: 4 }}>
                <div className="w-2 h-2 bg-[#a5400e] rounded-full flex-shrink-0 group-hover:scale-125 transition-transform"></div>
                <span className="text-sm text-[#5c2810] group-hover:text-[#a5400e] transition-colors">{reason}</span>
              </motion.div>
            ))}
          </motion.div>
          <motion.p className="text-sm text-[#7a4a3a] italic whitespace-pre-line bg-[#a5400e]/5 p-4 rounded-xl border-l-4 border-[#a5400e]" variants={fadeInUp}>
            {"Cette liste n’est pas exhaustive. N’hésitez pas à me contacter pour discuter de votre situation spécifique et déterminer si mon approche peut vous aider.\nCes séances relèvent du bien-être et ne remplacent ni un diagnostic, ni un traitement, ni un suivi médical ou psychologique. En cas de douleur, de symptômes persistants ou de difficulté liée à une addiction, adressez-vous à un professionnel de santé. Poursuivez les soins qui vous ont été prescrits."}
          </motion.p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.14 })}>
          <motion.div variants={scaleIn} whileHover={shouldReduceMotion ? undefined : { y: -8, transition: { duration: 0.3 } }}>
            <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#a5400e]/20 transition-colors">
                    <Clock className="text-[#a5400e]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-serif text-[#5c2810] mb-2 group-hover:text-[#a5400e] transition-colors">Durée</h4>
                    <p className="text-sm sm:text-base text-[#7a4a3a]">{consultationInfo.duration}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={scaleIn} whileHover={shouldReduceMotion ? undefined : { y: -8, transition: { duration: 0.3 } }}>
            <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-500">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#a5400e]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#a5400e]/20 transition-colors">
                    <Euro className="text-[#a5400e]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-serif text-[#5c2810] mb-2 group-hover:text-[#a5400e] transition-colors">Tarif</h4>
                    <p className="text-sm sm:text-base text-[#7a4a3a]">{consultationInfo.price}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div className="relative bg-gradient-to-br from-[#a5400e] via-[#d46a3e] to-[#a5400e] p-8 sm:p-10 rounded-2xl shadow-[0_20px_60px_rgb(165,64,14,0.25)] overflow-hidden mb-8" variants={fadeInRight}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3" />
          </div>
          <div className="relative flex flex-col sm:flex-row items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <Info className="text-white" size={24} />
            </div>
            <div>
              <h4 className="text-lg sm:text-xl font-serif text-white mb-2">Préparer votre séance</h4>
              <p className="text-sm sm:text-base text-white/90 leading-relaxed">{consultationInfo.preparation}</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="text-center bg-white/80 backdrop-blur-sm p-8 sm:p-10 rounded-2xl border border-[#a5400e]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" variants={fadeInUp}>
          <motion.p className="text-base sm:text-lg text-[#7a4a3a] mb-4 sm:mb-6 font-medium" variants={fadeInUp}>
            {consultationInfo.note}
          </motion.p>
          <motion.a
            href="#contact"
            whileHover={shouldReduceMotion ? undefined : { y: -3 }}
          >
            <Button className="bg-[#a5400e] hover:bg-[#7a2b0d] text-white px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#a5400e]/20">
              Prendre rendez-vous
            </Button>
          </motion.a>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ConsultationsSection;
